import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Landmark,
  Building2,
  Briefcase,
  CircleDollarSign,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { CTABanner } from "@/components/CTABanner";
import { IMAGES } from "@/lib/site";
import { ServicesCarousel } from "@/components/ServicesCarousel";
import { HeroMedia } from "@/components/HeroMedia";
import { ScrollCheck } from "@/components/ScrollCheck";

const PROCESS = [
  {
    title: "Assess",
    text: "We review your current core banking estate, pain points and target outcomes.",
    image: `${process.env.PUBLIC_URL}/media/delivery-assess.jpg`,
    alt: "Consulting team reviewing programme priorities",
  },
  {
    title: "Architect",
    text: "We design a pragmatic, standards-aligned solution and delivery roadmap.",
    image: `${process.env.PUBLIC_URL}/media/delivery-architect.jpg`,
    alt: "Abstract architecture representing solution design",
  },
  {
    title: "Deliver",
    text: "We execute with proven accelerators, automation and tight governance.",
    image: `${process.env.PUBLIC_URL}/media/delivery-deliver.jpg`,
    alt: "Data network representing governed technology delivery",
  },
  {
    title: "Sustain",
    text: "We run, support and continuously optimise your core post go-live.",
    image: `${process.env.PUBLIC_URL}/media/delivery-sustain.jpg`,
    alt: "Security technology representing managed support and resilience",
  },
];

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="home-hero relative overflow-hidden pt-32 md:pt-40 pb-20 md:pb-28 min-h-[600px] flex items-center">
        {/* Professional video background */}
        <HeroMedia />
        {/* Light overlays keep the theme bright and text readable */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/10 to-transparent" />
        <div className="absolute inset-0 bg-grid opacity-20" />

        <div className="relative w-full mx-auto max-w-7xl px-6 lg:px-12">
          <div className="hero-content max-w-2xl">
            <Reveal>
              <span className="specialist-gold-pulse inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur ring-1 ring-blue-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand">
                <ShieldCheck className="w-3.5 h-3.5" /> Core Banking Specialists
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-900">
                Modern Core Banking,<br />
                <span className="text-brand">delivered with confidence.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1} className="hero-copy">
              <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
                Sphere IT Solutions partners with Banks and Financial Institutions to
                architect, migrate, integrate, and modernize core banking platforms —
                combining deep banking expertise with the rigor, governance, and
                delivery discipline required for complex enterprise programmes.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="hero-actions">
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <Link to="/contact" data-testid="hero-contact-button">
                  <Button className="rounded-full bg-brand hover:bg-brand-hover text-white font-semibold gap-2 px-7 h-12 text-base w-full sm:w-auto shadow-lg shadow-blue-900/20">
                    Talk to an Expert <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/services" data-testid="hero-services-button">
                  <Button
                    variant="outline"
                    className="rounded-full border-2 border-slate-200 text-slate-900 hover:border-slate-900 bg-white/80 backdrop-blur font-semibold gap-2 px-7 h-12 text-base w-full sm:w-auto"
                  >
                    Explore Services <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRUSTED BY */}
      <section className="home-trusted relative">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 py-10">
          <p className="section-eyebrow gold-hover-label text-center text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">
            Trusted by banks and financial institutions worldwide
          </p>
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-6 items-center">
            {[
              { icon: Landmark, label: "Retail Banks", color: "text-blue-600" },
              { icon: Building2, label: "Corporate Banks", color: "text-indigo-600" },
              { icon: CircleDollarSign, label: "Wealth & Private", color: "text-cyan-600" },
              { icon: Briefcase, label: "FinTech & NBFI", color: "text-sky-600" },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-center gap-2.5">
                <item.icon className={`w-6 h-6 ${item.color}`} strokeWidth={1.5} />
                <span className="font-heading font-semibold text-sm text-slate-600">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US PREVIEW */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <img
                src="/media/home_how_we_do_optimized.avif"
                alt="Abstract architecture representing a robust core banking foundation"
                className="rounded-3xl w-full aspect-[4/3] object-cover shadow-xl ring-1 ring-slate-200"
                loading="lazy"
              />
            </Reveal>
            <Reveal delay={0.1}>
              <p className="section-eyebrow gold-hover-label text-sm uppercase tracking-[0.15em] font-semibold text-brand">Why Sphere IT Solutions</p>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Architecture-led delivery, banking-grade governance
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                We pair deep core banking engineering with genuine financial-services
                domain understanding — so your programme is de-risked from day one.
              </p>
              <ul className="mt-7 space-y-4">
                {[
                  "Guided by specialists with 20+ years of industry experience",
                  "Proven upgrade & migration accelerators",
                  "Regulatory-grade QA and release governance",
                  "SLA-backed managed support across time zones",
                ].map((point) => (
                  <li key={point} className="gold-check-row flex items-start gap-3">
                    <ScrollCheck className="w-5 h-5 text-brand mt-0.5 shrink-0" />
                    <span className="text-slate-700 font-medium">{point}</span>
                  </li>
                ))}
              </ul>
              <Link to="/why-us" data-testid="home-why-us-link">
                <Button className="mt-8 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold gap-2 px-6">
                  Why choose us <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-testid="services-preview-section">
        <img src={`${process.env.PUBLIC_URL}/media/core-banking-services.webp`} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-white/75" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div className="mb-14 flex flex-col items-center gap-6 text-center">
            <Reveal className="mx-auto max-w-2xl">
              <p className="section-eyebrow gold-hover-label text-sm uppercase tracking-[0.15em] font-semibold text-brand">What we do</p>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                End-to-end Core Banking expertise
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                From target-state architecture to managed support, we cover the full
                lifecycle of your core banking platform.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/services" data-testid="services-preview-all">
                <Button variant="outline" className="rounded-full border-2 border-slate-200 text-slate-900 hover:border-slate-900 bg-white font-semibold gap-2 px-7 h-12">
                  View all services <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </Reveal>
          </div>

          <ServicesCarousel />
        </div>
      </section>

      {/* DELIVERY APPROACH */}
      <section className="home-delivery py-20 md:py-28 relative overflow-hidden">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div
            className="overflow-visible rounded-[var(--radius-large)] px-6 py-12 sm:px-8 md:px-12 lg:px-14 lg:py-16"
            style={{
              backgroundColor: "#FBFAF7",
              backgroundImage: "radial-gradient(circle at 70% 20%, rgba(200, 164, 93, 0.07), transparent 50%)",
            }}
          >
            <Reveal className="mx-auto max-w-2xl text-center">
              <p className="section-eyebrow gold-hover-label text-sm uppercase tracking-[0.15em] font-semibold text-brand">How we deliver</p>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                A proven, low-risk delivery approach
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Every engagement follows a disciplined model designed to protect your
                core, your data and your timelines.
              </p>
            </Reveal>
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:-mx-[18%] lg:grid-cols-4">
              {PROCESS.map((step, i) => (
                <Reveal key={step.title} delay={i * 0.08}>
                  <div
                    data-testid={`delivery-card-${step.title.toLowerCase()}`}
                    className="flex h-full flex-col overflow-hidden rounded-[var(--radius-large)] bg-white transition-all duration-300 hover:-translate-y-1"
                    style={{ boxShadow: "0 28px 70px rgba(11, 31, 58, 0.18)" }}
                  >
                    <img
                      src={step.image}
                      alt={step.alt}
                      className="h-48 w-full object-cover sm:h-52 lg:h-48"
                      loading="lazy"
                    />
                    <div className="flex flex-1 flex-col p-7">
                      <h3 className="text-lg font-semibold text-brand font-heading">{step.title}</h3>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRY FOCUS */}
      <section className="home-banking py-20 md:py-28 relative overflow-hidden">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <p className="section-eyebrow gold-hover-label text-sm uppercase tracking-[0.15em] font-semibold text-brand">Banking focus</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Built for the realities of Core Banking
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              We support the full breadth of core banking functional domains — so whatever
              your programme touches, we bring hands-on expertise.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              {[
                { label: "Retail Banking", c: "bg-blue-50 text-blue-700 border-blue-100" },
                { label: "Corporate Banking", c: "bg-indigo-50 text-indigo-700 border-indigo-100" },
                { label: "Payments", c: "bg-cyan-50 text-cyan-700 border-cyan-100" },
                { label: "Lending", c: "bg-sky-50 text-sky-700 border-sky-100" },
                { label: "Wealth & Private", c: "bg-teal-50 text-teal-700 border-teal-100" },
                { label: "Treasury", c: "bg-violet-50 text-violet-700 border-violet-100" },
                { label: "Regulatory Reporting", c: "bg-blue-50 text-blue-700 border-blue-100" },
              ].map((t) => (
                <span key={t.label} className={`banking-label-depth rounded-full border px-4 py-2 text-sm font-semibold ${t.c}`}>
                  {t.label}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative">
              <img
                src= "/media/home_banking_focus.avif"
                alt="Data center infrastructure representing secure digital banking technology"
                className="relative rounded-3xl w-full aspect-[4/3] object-cover shadow-xl ring-1 ring-slate-200"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner />
    </div>
  );
};

export default Home;
